// NPM
import React, { useEffect, useState } from 'react';
import { Redirect, matchPath, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

// Components
import App from 'components/app/App';
import UpdateSecurityQuestions from 'components/updateSecurityQuestions/UpdateSecurityQuestions';
import FirstTimeLoginFlow from 'components/firstTimeLoginFlow/FirstTimeLoginFlow';
import LocaleProvider from 'components/locale/LocaleProvider';
import LoginWrapper from 'components/login/LoginWrapper';
import ResetPasswordPage from 'components/resetPassword/ResetPasswordPage';
import SecurityInformationPage from 'components/securityInformation/SecurityInformationPage';
import SessionExpiration from 'components/sessionExpiration/SessionExpiration';
import Splash from 'components/splash/Splash';
import SSOLogin from 'components/ssoLogin/SSOLogin';
import TermAndConditions from 'components/termsAndConditions/TermsAndConditions';
import UserEmulation from 'components/userEmulation/UserEmulation';
import { useAppConfig } from 'components/appConfig/AppConfig';
import InfuseHandShakeScreen from 'infuse/InfuseHandShakeScreen';
import InfuseSpot from 'infuse/InfuseSpot';
import withRouterConnect from 'components/withRouterConnect/withRouterConnect';
import { INFUSE, WORKFLOWS } from 'constants.js';

// Utils and services
import { getUser, logout } from 'logic/store/authentication/actions';
import { setSsoPageNavigated } from 'logic/store/sso/actions';
import { setFavicon, setTitle } from 'utils/dynamicHeaderAttributes';
import { UserShape } from 'api/user/props';
import { setDeviceCode } from 'api/user/service';
import { clearGrids } from 'logic/store/grids/actions';

import loadThirdPartyScripts from './thirdPartyScripts';

export function BootstrapComponent({
  enableGlia,
  getUser,
  infuse,
  liveEngageKey,
  logout,
  navigatedToSsoPage,
  setSsoPageNavigated,
  ssoConfig,
  theme,
  themeReady,
  user,
  userStatusKnown
}) {
  useEffect(() => {
    setDeviceCode();
  }, []);

  if (user && user.timeZone) {
    moment.tz.setDefault(user.timeZone);
  }

  const config = useAppConfig();
  const { isWeb } = config;
  const { title, images: { favicon } = {} } = theme || {};
  const [handshakeFlag, setHandShakeFlag] = useState(false);
  const history = useHistory();

  if (favicon) {
    setFavicon(favicon);
  }
  if (title) {
    setTitle(title);
  }

  if (!userStatusKnown || !themeReady || !config || !Object.keys(config).length) {
    return <Splash />;
  }

  if (!user) {
    if (!ssoConfig?.isSSOEnabled || !ssoConfig?.loginURL) {
      return (
        <LocaleProvider>
          <InfuseSpot />
          <LoginWrapper />
        </LocaleProvider>
      );
    }
    if (!navigatedToSsoPage) {
      try {
        const url = new URL(ssoConfig.loginURL);
        window.location.replace(url);
        setSsoPageNavigated(true);
      } catch {
        const match = matchPath(history.location?.pathname, {
          path: WORKFLOWS.SSO_LOGIN,
          exact: true,
          strict: false
        });
        if (match) {
          setSsoPageNavigated(true);
          return (
            <LocaleProvider>
              <SSOLogin />
            </LocaleProvider>
          );
        }
        return <Redirect to={ssoConfig.loginURL} />;
      }
    }
    return null;
  }

  if (!user.emulationSession) {
    // emulation
    if (user.bankUser) {
      loadThirdPartyScripts({ liveEngageKey, enableGlia, isWeb, user });
      return (
        <LocaleProvider>
          <UserEmulation />
          <SessionExpiration emulation />
        </LocaleProvider>
      );
    }

    if (
      config.isWeb &&
      !user.bankUser &&
      !infuse?.state?.[infuse?.config?.snippetId]?.closed &&
      infuse?.config?.handShakeEnabled !== INFUSE.DISABLED &&
      !handshakeFlag
    ) {
      return <InfuseHandShakeScreen setHandShakeFlag={setHandShakeFlag} logout={logout} />;
    }

    if (user.hasTermsAndConditions) {
      loadThirdPartyScripts({ liveEngageKey, enableGlia, isWeb, user });
      return (
        <LocaleProvider>
          <TermAndConditions user={user} />
          <SessionExpiration />
        </LocaleProvider>
      );
    }

    if (user.isFirstLogin && !user.ssoSession) {
      loadThirdPartyScripts({ liveEngageKey, enableGlia, isWeb, user });
      return (
        <LocaleProvider>
          <div className="glu-content">
            <FirstTimeLoginFlow user={user} />
            <SessionExpiration />
          </div>
        </LocaleProvider>
      );
    }

    if (user.isSecurityAnsNeeded) {
      loadThirdPartyScripts({ liveEngageKey, enableGlia, isWeb, user });
      return (
        <LocaleProvider>
          <div className="glu-content">
            <UpdateSecurityQuestions />
            <SessionExpiration />
          </div>
        </LocaleProvider>
      );
    }

    if (user.securityContactRequired || user.reconfirmSecurityContacts) {
      loadThirdPartyScripts({ liveEngageKey, enableGlia, isWeb, user });
      return (
        <LocaleProvider>
          <div className="glu-content">
            <SecurityInformationPage onCancel={logout} onSuccess={getUser} returnUrl={false} />
            <SessionExpiration />
          </div>
        </LocaleProvider>
      );
    }

    if (user.mustChangePassword) {
      loadThirdPartyScripts({ liveEngageKey, enableGlia, isWeb, user });
      return (
        <LocaleProvider>
          <div className="glu-content">
            <ResetPasswordPage user={user} onSuccess={getUser} returnUrl={false} />
            <SessionExpiration />
          </div>
        </LocaleProvider>
      );
    }
  }
  loadThirdPartyScripts({ liveEngageKey, enableGlia, isWeb, user });
  return (
    <LocaleProvider>
      <App />
      <SessionExpiration />
    </LocaleProvider>
  );
}

BootstrapComponent.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  enableGlia: PropTypes.any,
  getUser: PropTypes.func.isRequired,
  liveEngageKey: PropTypes.string,
  logout: PropTypes.func,
  theme: PropTypes.shape({}).isRequired,
  themeReady: PropTypes.bool,
  user: UserShape,
  userStatusKnown: PropTypes.bool.isRequired,
  infuse: PropTypes.shape({
    config: PropTypes.shape({
      snippetId: PropTypes.string.isRequired,
      handShakeEnabled: PropTypes.string.isRequired
    }),
    state: PropTypes.shape({})
  }).isRequired,
  ssoConfig: PropTypes.shape({ isSSOEnabled: PropTypes.bool, loginURL: PropTypes.string }),
  setSsoPageNavigated: PropTypes.func,
  navigatedToSsoPage: PropTypes.bool
};

BootstrapComponent.defaultProps = {
  enableGlia: false,
  logout() {},
  liveEngageKey: '',
  themeReady: false,
  user: null,
  ssoConfig: null,
  setSsoPageNavigated() {},
  navigatedToSsoPage: false
};

/* istanbul ignore next */
const mapStateToProps = (state) => ({
  user: state.auth.user,
  infuse: state?.infuse || {},
  userStatusKnown: state.auth.statusKnown,
  liveEngageKey: state.auth.liveEngageKey,
  enableGlia: state.auth.enableGlia,
  ssoConfig: state.sso.ssoConfig,
  navigatedToSsoPage: state.sso.navigatedToSsoPage
});

const mapDispatchToProps = (dispatch) => ({
  getUser: (...params) => dispatch(getUser(...params)),
  logout: (...params) => dispatch(logout(...params)),
  clearGrids: () => clearGrids(dispatch),
  setSsoPageNavigated: () => setSsoPageNavigated(dispatch)
});

export default withRouterConnect(mapStateToProps, mapDispatchToProps)(BootstrapComponent);
